/* jshint esversion: 8 */
/* jshint ignore:start */
import React, { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { FiPhoneCall, FiVideo, FiRefreshCcw } from "react-icons/fi";
import { MdCallMade, MdCallReceived } from "react-icons/md";
import { MyContext } from "../Context/MyProvider";
import LodingLottie from "../Lodinglottie";
import { showTost } from "../showTost";
import PageHeader from "./PageHeader";

const CallHistory = () => {
  const { basUrl, imageBaseURL } = useContext(MyContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const userId = () => {
    try {
      return JSON.parse(localStorage.getItem("Register_User") || "{}").id;
    } catch {
      return null;
    }
  };

  const imageUrl = (path) => {
    const value = String(path || "").trim();
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    return `${imageBaseURL}${value.replace(/^\/+/, "")}`;
  };

  const fetchHistory = useCallback(() => {
    const uid = userId();
    if (!uid) {
      setError("Login required");
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    setError("");

    axios
      .post(
        `${basUrl}call_history`,
        { uid },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      )
      .then((res) => {
        if (res.data?.Result === "true") {
          setItems(Array.isArray(res.data.call_history) ? res.data.call_history : []);
        } else {
          setItems([]);
          setError(res.data?.ResponseMsg || "Unable to load call history.");
        }
      })
      .catch((err) => {
        console.error("Call history error:", err);
        setItems([]);
        setError("Unable to load call history.");
        showTost({ title: "Unable to load call history." });
      })
      .finally(() => setLoading(false));
  }, [basUrl]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const statusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "completed":
        return "bg-green-50 text-green-600";
      case "accepted":
      case "ringing":
        return "bg-purple-50 text-[rgba(152,14,255,255)]";
      case "rejected":
      case "cancelled":
      case "missed":
        return "bg-red-50 text-red-500";
      default:
        return "bg-gray-100 text-gray-500";
    }
  };

  const formatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString([], {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const duration = (value) => {
    const seconds = Number(value || 0);
    if (seconds <= 0) return "0 sec";
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`;
  };

  const title = (item) => String(item?.other_name || "").trim() || "Unknown user";

  return (
    <div className="main-wrapper bg-[#e5e5e5] dashboard">
      <div className="content-body">
        <div className="container-fluid my-2 sm:my-4 px-2.5 sm:px-4">
          <PageHeader
            title="Call History"
            subtitle="Audio and video calls from your matches"
            breadcrumbs={[{ label: "Call History" }]}
            icon={FiPhoneCall}
            actions={
              <button
                onClick={fetchHistory}
                className="px-4 py-2 rounded-xl font-semibold text-sm border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 transition-all shadow-xs cursor-pointer flex items-center gap-2"
              >
                <FiRefreshCcw />
                <span>Refresh</span>
              </button>
            }
          />

          <div className="bg-white rounded-[10px] p-[20px] min-h-[520px]">
            {loading ? (
              <div className="h-[420px] flex flex-col items-center justify-center">
                <LodingLottie ClassCss="w-[82px] h-[82px]" />
                <p className="m-0 mt-[10px] text-gray-500">Loading...</p>
              </div>
            ) : error ? (
              <div className="h-[420px] flex flex-col items-center justify-center text-center">
                <div className="w-[78px] h-[78px] rounded-full bg-[#f5e8ff] flex items-center justify-center mb-[16px]">
                  <FiPhoneCall className="text-[rgba(152,14,255,255)] w-[36px] h-[36px]" />
                </div>
                <h4 className="mb-[6px]">Call History</h4>
                <p className="m-0 text-gray-500">{error}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="h-[420px] flex flex-col items-center justify-center text-center">
                <div className="w-[78px] h-[78px] rounded-full bg-[#f5e8ff] flex items-center justify-center mb-[16px]">
                  <FiPhoneCall className="text-[rgba(152,14,255,255)] w-[36px] h-[36px]" />
                </div>
                <h4 className="mb-[6px]">No call history yet</h4>
                <p className="m-0 text-gray-500">Your audio and video calls will appear here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 max-_1500_:grid-cols-2 max-_768_:grid-cols-1 gap-[16px]">
                {items.map((item) => {
                  const isIncoming = String(item.direction || "").toLowerCase() === "incoming";
                  const isVideo = String(item.type || "").toLowerCase() === "video";
                  const avatar = imageUrl(item.other_pic);

                  return (
                    <div key={item.id || item.call_id} className="border border-[#eadcf5] rounded-[12px] p-[16px] bg-white">
                      <div className="flex items-start gap-[14px]">
                        <div className="w-[52px] h-[52px] rounded-full bg-[#f5e8ff] overflow-hidden flex items-center justify-center text-[rgba(152,14,255,255)] font-[800]">
                          {avatar ? (
                            <img src={avatar} alt={title(item)} className="w-full h-full object-cover" />
                          ) : (
                            title(item).charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-[10px]">
                            <h6 className="m-0 text-[17px] truncate">{title(item)}</h6>
                            <span className={`rounded-full px-[10px] py-[4px] text-[12px] font-[700] ${statusColor(item.status)}`}>
                              {item.status || "Unknown"}
                            </span>
                          </div>
                          <div className="mt-[8px] flex flex-wrap items-center gap-[8px] text-gray-500">
                            <span className="inline-flex items-center gap-[4px]">
                              {isIncoming ? <MdCallReceived className="text-green-500" /> : <MdCallMade className="text-[rgba(152,14,255,255)]" />}
                              {isIncoming ? "Incoming" : "Outgoing"}
                            </span>
                            <span className="inline-flex items-center gap-[4px]">
                              {isVideo ? <FiVideo /> : <FiPhoneCall />}
                              {isVideo ? "Video" : "Audio"}
                            </span>
                          </div>
                          <div className="mt-[10px] flex flex-wrap items-center justify-between gap-[8px] text-[13px]">
                            <span className="text-gray-500">{formatDate(item.started_at)}</span>
                            <span className="font-[700] text-[rgba(152,14,255,255)]">{duration(item.duration_seconds)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallHistory;
